import "./PersistentAudioPlayer.css";

import { Controller } from "@hotwired/stimulus";
import { DomEvents } from "../Generated/DomEvents";
import { logError } from "../../../../Libraries/CoreWeb/CoreWeb/Features/Shared/logging";
import { isNavigationAbort } from "../NavigationAbort";
import {
    applyMedia,
    getMediaRevision,
    releaseMedia,
    seekAudioBack,
    seekAudioToProgress,
    showPlayer,
    syncPlayerVisibility,
    updatePlaybackState,
    type PersistentAudioMedia
} from "./PersistentAudioPlayerUi";

export default class PersistentAudioPlayerController extends Controller<HTMLElement> {
    static targets = ["audio"];

    declare readonly audioTarget: HTMLAudioElement;
    private playbackAttempt = 0;

    connect(): void {
        document.addEventListener(DomEvents.persistentAudioPlayRequested, this.onPlayRequested as EventListener);
        this.audioTarget.addEventListener("play", this.onMediaEvent);
        this.audioTarget.addEventListener("pause", this.onMediaEvent);
        this.audioTarget.addEventListener("timeupdate", this.onMediaEvent);
        this.audioTarget.addEventListener("durationchange", this.onMediaEvent);
        this.audioTarget.addEventListener("ended", this.onEnded);
        this.element.dataset.instanceId ||= createInstanceId();
        syncPlayerVisibility(this.element);
        updatePlaybackState(this.element, this.audioTarget);
    }

    disconnect(): void {
        ++this.playbackAttempt;
        document.removeEventListener(DomEvents.persistentAudioPlayRequested, this.onPlayRequested as EventListener);
        this.audioTarget.removeEventListener("play", this.onMediaEvent);
        this.audioTarget.removeEventListener("pause", this.onMediaEvent);
        this.audioTarget.removeEventListener("timeupdate", this.onMediaEvent);
        this.audioTarget.removeEventListener("durationchange", this.onMediaEvent);
        this.audioTarget.removeEventListener("ended", this.onEnded);
    }

    async toggle(): Promise<void> {
        if (this.audioTarget.paused) {
            await this.playAudio("toggle");
        } else {
            ++this.playbackAttempt;
            this.audioTarget.pause();
            updatePlaybackState(this.element, this.audioTarget);
        }
    }

    replay(): void {
        seekAudioBack(this.audioTarget);
        updatePlaybackState(this.element, this.audioTarget);
    }

    seek(event: Event): void {
        if (!(event.currentTarget instanceof HTMLInputElement)) {
            return;
        }

        seekAudioToProgress(this.audioTarget, Number.parseFloat(event.currentTarget.value));
        updatePlaybackState(this.element, this.audioTarget);
    }

    close(): void {
        ++this.playbackAttempt;
        releaseMedia(this.element, this.audioTarget);
    }

    private readonly onPlayRequested = (event: CustomEvent<PersistentAudioMedia>): void => {
        const media = event.detail;
        if (!media?.id || !media.url) {
            return;
        }

        applyMedia(this.element, this.audioTarget, media);

        showPlayer(this.element);
        void this.playAudio("request");
        updatePlaybackState(this.element, this.audioTarget);
    };

    private async playAudio(playbackAction: "toggle" | "request"): Promise<void> {
        const audio = this.audioTarget;
        const attempt = ++this.playbackAttempt;
        const revision = getMediaRevision(audio);
        const mediaId = audio.dataset.mediaId;
        const mediaUrl = audio.src;
        const isCurrent = (): boolean => attempt === this.playbackAttempt && revision === getMediaRevision(audio);
        try {
            if (audio.error) audio.load();
            await audio.play();
        } catch (error) {
            if (isNavigationAbort(error)) return;
            const current = isCurrent();
            const diagnostics = current ? {
                currentSrc: audio.currentSrc,
                mediaErrorCode: audio.error?.code ?? null,
                mediaErrorMessage: audio.error?.message ?? null,
                networkState: audio.networkState,
                readyState: audio.readyState
            } : {};
            if (current) {
                audio.pause();
                updatePlaybackState(this.element, audio);
            }
            await logError(error, {
                component: "persistent-audio-player", playbackAction,
                mediaId, mediaUrl, ...diagnostics, staleAttempt: !current
            }).catch(() => undefined);
        } finally {
            if (isCurrent()) updatePlaybackState(this.element, audio);
        }
    }

    private readonly onMediaEvent = (): void => {
        updatePlaybackState(this.element, this.audioTarget);
    };

    private readonly onEnded = (): void => {
        if (this.audioTarget.dataset.mediaId && this.audioTarget.ended) {
            releaseMedia(this.element, this.audioTarget);
        }
    };
}

function createInstanceId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `persistent-audio-${Math.random().toString(36).slice(2)}`;
}
